import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { RefreshCw, Loader, Monitor, Smartphone, Globe, MapPin, Settings, Info } from 'lucide-react'
import { useApi, asArray } from '../hooks/useApi'
import { ContentHeader } from '../components/layout/ContentHeader'
import StatsCards from '../components/rankings/StatsCards'
import PositionDistribution from '../components/rankings/PositionDistribution'
import EvolutionChart from '../components/rankings/EvolutionChart'
import ProgressBar from '../components/rankings/ProgressBar'
import DistributionTable from '../components/rankings/DistributionTable'
import KeywordTable from '../components/rankings/KeywordTable'
import PagesSection from '../components/rankings/PagesSection'
import CompetitorsSection from '../components/rankings/CompetitorsSection'
import UsageCard from '../components/rankings/UsageCard'
import NotesSection from '../components/rankings/NotesSection'
import KeywordBlocks from '../components/rankings/KeywordBlocks'
import DateRangePicker from '../components/rankings/DateRangePicker'

interface RankingKeywordRow {
  id: number
  keyword: string
  device: string
  location: string
  position: number | null
  url: string | null
  delta: number | null
  volume: number | null
  manualPosition: number | null
  checkedAt: string | null
}

interface Distribution {
  top1: number
  top3: number
  top5: number
  top10: number
  top30: number
  notRanked: number
}

interface PageData {
  url: string
  keywordCount: number
  bestPosition: number | null
  avgPosition: number | null
}

interface SerpCompetitor {
  id: number
  domain: string
  isManual: boolean
  occurrences: number
}

interface QuotaData {
  allowed: boolean
  used: number
  dailyLimit: number
  remaining: number
}

interface SearchSettings {
  country: string
  locationCity: string | null
  language: string
  device: string
}

interface HistoryEntry {
  date: string
  top1: number
  top3: number
  top5: number
  top10: number
  top30: number
  top100: number
  total: number
}

interface RankingsData {
  keywords: RankingKeywordRow[]
  visibility: number
  distribution: Distribution
  distributionHistory: HistoryEntry[]
  datesWithData: string[]
  pages: PageData[]
  competitors: SerpCompetitor[]
  quota: QuotaData
  domain: string
  searchSettings: SearchSettings
}

interface UsageSummary {
  today: number
  month: number
  dailyLimit: number
  monthlyLimit: number
  last7Days: Array<{ day: string; total: number }>
}

interface CheckStatus {
  status: 'idle' | 'running' | 'done'
  checked: number
  total: number
}

type DateRange = '7j' | '30j' | '90j' | 'custom'

function InfoTooltip({ text }: { text: string }) {
  return (
    <span className="relative group ml-1.5 cursor-help">
      <Info className="w-3.5 h-3.5 text-[#8B8FA7] inline" strokeWidth={1.25} />
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#1B2559] text-white text-[11px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
        {text}
      </span>
    </span>
  )
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0]
}

function formatDateDisplay(d: string): string {
  const date = new Date(d)
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
}

function getDateRangeValues(range: DateRange, customFrom?: string, customTo?: string): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  if (range === 'custom' && customFrom && customTo) return { from: customFrom, to: customTo }
  if (range === '7j') from.setDate(to.getDate() - 7)
  else if (range === '30j') from.setDate(to.getDate() - 30)
  else if (range === '90j') from.setDate(to.getDate() - 90)
  return { from: formatDate(from), to: formatDate(to) }
}

function countryFlag(code: string): string {
  if (!code || code.length !== 2) return ''
  const upper = code.toUpperCase()
  return String.fromCodePoint(...[...upper].map(c => 0x1F1E6 + c.charCodeAt(0) - 65))
}

function deviceIcon(device: string) {
  if (device === 'mobile') return <Smartphone className="w-3.5 h-3.5" strokeWidth={1.25} />
  return <Monitor className="w-3.5 h-3.5" strokeWidth={1.25} />
}

function Sk({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-lg bg-[#E2E4ED] ${className}`} />
}

function RankingsSkeleton() {
  return (
    <div className="space-y-4">
      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map(i => (
          <div key={i} className="bg-white border border-[#E2E4ED] rounded-xl p-5 space-y-3">
            <Sk className="h-3 w-24" />
            <Sk className="h-8 w-16" />
            <Sk className="h-2.5 w-32" />
          </div>
        ))}
      </div>

      {/* Distribution block */}
      <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
        <div className="flex gap-6">
          <div className="w-1/3 grid grid-cols-2 gap-px bg-[#E2E4ED] rounded-xl overflow-hidden">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} className="bg-white p-3 space-y-2">
                <Sk className="h-2.5 w-12" />
                <Sk className="h-8 w-full" />
                <Sk className="h-5 w-8" />
              </div>
            ))}
          </div>
          <div className="flex-1">
            <Sk className="h-[280px] w-full" />
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white border border-[#E2E4ED] rounded-xl p-4">
        <Sk className="h-4 w-full" />
      </div>

      {/* Keyword table */}
      <div className="bg-white border border-[#E2E4ED] rounded-xl overflow-hidden">
        <div className="px-5 pt-5 pb-3 space-y-3">
          <div className="flex items-center justify-between">
            <Sk className="h-4 w-40" />
            <Sk className="h-8 w-24" />
          </div>
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4].map(i => <Sk key={i} className="h-7 w-20" />)}
          </div>
          <Sk className="h-9 w-full" />
        </div>
        <div className="border-t border-[#E2E4ED]">
          {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
            <div key={i} className="flex items-center gap-4 px-5 py-3 border-b border-[#E2E4ED]/60">
              <Sk className="h-4 flex-1 max-w-[200px]" />
              <Sk className="h-6 w-10 rounded-lg" />
              <Sk className="h-4 w-10" />
              <Sk className="h-4 w-10" />
              <Sk className="h-4 flex-1 max-w-[180px]" />
              <Sk className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function Rankings() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [checkState, setCheckState] = useState<CheckStatus | null>(null)
  const [dateRange, setDateRange] = useState<DateRange>('30j')
  const [customFrom, setCustomFrom] = useState(formatDate(new Date()))
  const [customTo, setCustomTo] = useState(formatDate(new Date()))
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const dateValues = getDateRangeValues(dateRange, customFrom, customTo)

  const {
    data: rankingsData,
    loading: rankingsLoading,
    error: rankingsError,
    refetch,
  } = useApi<RankingsData>(
    `/api/keywords/clients/${projectId}?from=${dateValues.from}&to=${dateValues.to}`
  )

  const { data: usageData } = useApi<UsageSummary>(
    `/api/usage/clients/${projectId}?service=serper`
  )

  if (rankingsError === 'Unauthorized' || rankingsError === '401') {
    navigate('/login')
    return null
  }

  const keywords = asArray(rankingsData?.keywords)
  const visibility = rankingsData?.visibility ?? 0
  const distribution = rankingsData?.distribution ?? { top1: 0, top3: 0, top5: 0, top10: 0, top30: 0, notRanked: 0 }
  const pages = asArray(rankingsData?.pages)
  const competitors = asArray(rankingsData?.competitors)
  const quota = rankingsData?.quota ?? { allowed: true, used: 0, dailyLimit: 100, remaining: 100 }
  const searchSettings = rankingsData?.searchSettings
  const domain = rankingsData?.domain

  const maxKeywords = 50

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const startPolling = useCallback(() => {
    stopPolling()
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/keywords/check-status/${projectId}`, { credentials: 'include' })
        const json = await res.json()
        const status = json.data ?? json
        setCheckState(status)
        if (status.status === 'done' || status.status === 'idle') {
          stopPolling()
          refetch()
          setTimeout(() => setCheckState(null), 2000)
        }
      } catch {
        stopPolling()
      }
    }, 2000)
  }, [projectId, stopPolling, refetch])

  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  async function handleCheckAll() {
    try {
      const res = await fetch(`/api/keywords/check/${projectId}`, {
        method: 'POST',
        credentials: 'include',
      })
      const json = await res.json()
      if (res.ok) {
        setCheckState({ status: 'running', checked: 0, total: keywords.length })
        startPolling()
      } else {
        setCheckState(null)
        alert(json.error || 'Erreur')
      }
    } catch {
      alert('Erreur reseau')
    }
  }

  async function handleAddKeyword(keyword: string) {
    setAdding(true)
    setAddError(null)
    try {
      const res = await fetch(`/api/keywords/clients/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ keyword }),
      })
      const json = await res.json()
      if (!res.ok) setAddError(json.error || 'Erreur')
      else refetch()
    } catch {
      setAddError('Erreur reseau')
    } finally {
      setAdding(false)
    }
  }

  async function handleDeleteKeyword(id: number) {
    await fetch(`/api/keywords/${id}`, { method: 'DELETE', credentials: 'include' })
    refetch()
  }

  async function handleManualPosition(id: number, position: number | null) {
    const res = await fetch(`/api/keywords/${id}/manual-position`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ position }),
    })
    const json = await res.json()
    if (!res.ok) throw new Error(json.error || 'Erreur')
    refetch()
  }

  async function handleAddCompetitor(domain: string) {
    await fetch(`/api/keywords/competitors/${projectId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ domain }),
    })
    refetch()
  }

  async function handleDeleteCompetitor(id: number) {
    await fetch(`/api/keywords/competitors/${id}`, { method: 'DELETE', credentials: 'include' })
    refetch()
  }

  // chartData removed — EvolutionChart fetches its own data

  const isChecking = checkState?.status === 'running'
  const checkProgress = checkState && checkState.total > 0
    ? Math.round((checkState.checked / checkState.total) * 100)
    : 0

  const totalDelta = keywords.reduce((sum, kw) => sum + (kw.delta ?? 0), 0)

  // Extract city display name (remove ",Grand Est,France" etc)
  const cityDisplay = searchSettings?.locationCity?.split(',')[0] || null

  const subtitleNode = domain ? (
    <div className="flex items-center gap-2 text-sm text-[#8B8FA7]">
      <Globe className="w-3.5 h-3.5" strokeWidth={1.25} />
      <span className="font-medium text-[#555A73]">{domain}</span>
      {searchSettings && (
        <>
          <span className="text-[#E2E4ED]">|</span>
          <span className="text-base leading-none">{countryFlag(searchSettings.country)}</span>
          <span>{searchSettings.country?.toUpperCase()}</span>
          {cityDisplay && (
            <>
              <MapPin className="w-3 h-3" strokeWidth={1.25} />
              <span className="text-[#555A73]">{cityDisplay}</span>
            </>
          )}
          <span className="text-[#E2E4ED]">|</span>
          <span className="inline-flex items-center gap-1">
            {deviceIcon(searchSettings.device)}
            <span className="capitalize">{searchSettings.device}</span>
          </span>
          <span className="text-[#E2E4ED]">|</span>
          <Link
            to={`/p/${projectId}/settings/search-engines`}
            className="inline-flex items-center gap-1 text-orion-primary hover:text-orion-primary-hover transition-colors"
          >
            <Settings className="w-3 h-3" strokeWidth={1.25} />
            <span className="text-xs">Parametres</span>
          </Link>
        </>
      )}
    </div>
  ) : undefined

  return (
    <div className="space-y-4">
      <ContentHeader
        breadcrumb={[
          { label: 'Dashboard', to: `/p/${projectId}` },
          { label: 'Classements' },
        ]}
        title="Classement des mots-cles"
        subtitle={subtitleNode as any}
        actions={
          <div className="flex items-center gap-3">
            {/* Date range: pills + calendar inputs */}
            <div className="flex items-center gap-1.5 bg-[#F4F5FA] rounded-xl p-0.5">
              {(['7j', '30j', '90j'] as DateRange[]).map(range => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    dateRange === range
                      ? 'bg-white text-[#1B2559] shadow-sm'
                      : 'text-[#8B8FA7] hover:text-[#555A73]'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
            <DateRangePicker
              from={dateValues.from}
              to={dateValues.to}
              datesWithData={asArray(rankingsData?.datesWithData)}
              onChange={(from, to) => {
                setDateRange('custom')
                setCustomFrom(from)
                setCustomTo(to)
              }}
            />

            {isChecking && (
              <div className="flex items-center gap-2.5">
                <div className="w-32 h-1.5 rounded-full bg-[#E2E4ED] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                    style={{ width: `${checkProgress}%` }}
                  />
                </div>
                <span className="text-xs text-[#555A73] whitespace-nowrap tabular-nums">
                  {checkState!.checked}/{checkState!.total}
                </span>
              </div>
            )}
            {checkState?.status === 'done' && (
              <span className="text-xs font-medium text-emerald-600">Termine</span>
            )}
            <button
              onClick={handleCheckAll}
              disabled={isChecking || !quota.allowed || keywords.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
              title={!quota.allowed ? `Quota atteint (${quota.used}/${quota.dailyLimit})` : 'Reverifier toutes les positions'}
            >
              {isChecking ? (
                <Loader className="w-4 h-4 animate-spin" strokeWidth={1.25} />
              ) : (
                <RefreshCw className="w-4 h-4" strokeWidth={1.25} />
              )}
              {isChecking ? `Verification ${checkState!.checked}/${checkState!.total}...` : 'Reverifier tout'}
            </button>
          </div>
        }
      />

      {rankingsLoading ? (
        <RankingsSkeleton />
      ) : (
        <>
          {/* 3 Stats Cards */}
          <StatsCards
            keywords={keywords}
            distribution={distribution}
            visibility={visibility}
          />

          {/* Distribution cards + Stacked area chart */}
          <div>
            <div className="flex items-center mb-2">
              <h3 className="text-sm font-semibold text-[#1B2559]">Répartition des top positions</h3>
              <InfoTooltip text="Distribution des mots-clés par tranche de position dans les résultats Google" />
            </div>
            <PositionDistribution distribution={distribution} history={asArray(rankingsData?.distributionHistory)} />
          </div>

          {/* Evolution chart — 4 metriques */}
          <div>
            <div className="flex items-center mb-2">
              <h3 className="text-sm font-semibold text-[#1B2559]">Evolution</h3>
              <InfoTooltip text="Evolution des metriques de classement dans le temps" />
            </div>
            <EvolutionChart projectId={projectId!} />
          </div>

          {/* Colored progress bar */}
          <ProgressBar distribution={distribution} totalDelta={totalDelta} />

          {/* Detailed distribution table */}
          <DistributionTable
            distribution={distribution}
            totalKeywords={keywords.length}
          />

          {/* Keyword insight blocks */}
          <KeywordBlocks
            keywords={keywords}
            onFilterChange={(filter) => {
              // Navigate to detailed view with filter
              navigate(`/p/${projectId}/rankings/detailed?filter=${filter}`)
            }}
          />

          {/* Keyword Table */}
          <div>
            <div className="flex items-center mb-2">
              <h3 className="text-sm font-semibold text-[#1B2559]">Présentation des mots-clés</h3>
              <InfoTooltip text="Liste complète des mots-clés suivis avec leur position actuelle et évolution" />
            </div>
            <KeywordTable
              keywords={keywords}
              onAdd={handleAddKeyword}
              onDelete={handleDeleteKeyword}
              onManualPosition={handleManualPosition}
              adding={adding}
              addError={addError}
              projectId={projectId!}
              maxKeywords={maxKeywords}
              searchSettings={searchSettings}
            />
          </div>

          {/* Pages + Competitors side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center mb-2">
                <h3 className="text-sm font-semibold text-[#1B2559]">Pages</h3>
                <InfoTooltip text="Pages de votre site qui apparaissent dans les résultats de recherche" />
              </div>
              <PagesSection pages={pages} />
            </div>
            <div>
              <div className="flex items-center mb-2">
                <h3 className="text-sm font-semibold text-[#1B2559]">Concurrents</h3>
                <InfoTooltip text="Domaines concurrents détectés automatiquement dans les mêmes résultats de recherche" />
              </div>
              <CompetitorsSection
                competitors={competitors}
                onAdd={handleAddCompetitor}
                onDelete={handleDeleteCompetitor}
              />
            </div>
          </div>

          {/* Consommation API */}
          <div>
            <div className="flex items-center mb-2">
              <h3 className="text-sm font-semibold text-[#1B2559]">Consommation API</h3>
              <InfoTooltip text="Crédits DataForSEO utilisés pour les vérifications de position" />
            </div>
            <UsageCard
              manualChecks={{ used: quota.used, limit: quota.dailyLimit }}
              keywordLimits={{ used: keywords.length, limit: maxKeywords }}
            />
          </div>

          {/* Notes */}
          <NotesSection />
        </>
      )}
    </div>
  )
}
